"use client"

import * as React from "react"
import { Check, Copy, Loader2, Mail, MoreHorizontal, Send, Instagram } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { useToast } from "@/hooks/use-toast"
import { formatPrice } from "@/lib/utils"
import { SQUARE_LG, CARD_SM, imagePresetUrl } from "@/lib/images/presets"
import {
  generateProfileStoryCardDataUrl,
  generateStoryCardDataUrl,
  resolveStoryContext,
  type ListingShareMeta,
  type ProfileStoryCardInput,
  type StoryCardContext,
} from "@/lib/story-cards"

const MESSENGER_LOGO_URL = "https://upload.wikimedia.org/wikipedia/commons/6/63/Facebook_Messenger_logo_2025.svg"

function deriveInitials(primary?: string, secondary?: string): string {
  const pick = (value?: string) => {
    if (!value) return ""
    const trimmed = value.replace(/^@+/, "").trim()
    if (!trimmed) return ""
    const words = trimmed.split(/[\s._-]+/).filter(Boolean)
    if (words.length >= 2) {
      return `${words[0]![0] ?? ""}${words[1]![0] ?? ""}`
    }
    return trimmed.slice(0, 2)
  }
  const candidate = pick(primary) || pick(secondary)
  return (candidate || "?").toUpperCase()
}

interface ProfileAvatarPreviewProps {
  image?: string
  username?: string
  title?: string
  size?: "sm" | "lg"
}

function ProfileAvatarPreview({ image, username, title, size = "lg" }: ProfileAvatarPreviewProps) {
  const [broken, setBroken] = React.useState(false)
  React.useEffect(() => {
    setBroken(false)
  }, [image])

  const dimensions = size === "lg" ? "w-24 h-24 text-2xl" : "w-14 h-14 text-base"
  const showImage = Boolean(image) && !broken
  const initials = deriveInitials(username, title)
  const displayName = username ? `@${username.replace(/^@+/, "")}` : title

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className={`${dimensions} rounded-full overflow-hidden relative shrink-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold select-none`}
      >
        {showImage ? (
          <img
            // `w-24` (lg) / `w-14` = 96 CSS px at the largest -> SQUARE_LG.
            // Guard on `image`, not on the built URL: `imagePresetUrl` takes a
            // non-nullable ref and resolves relative paths itself (see
            // `toSupabasePublicImageThumbUrl` — the resolve lives in the funnel,
            // not at the render point), so an outer `resolveStorageUrl` here was
            // both redundant and the thing that widened the type to include
            // `undefined`. `showImage` already proves it is set at runtime.
            src={image ? imagePresetUrl(image, SQUARE_LG) : undefined}
            alt={displayName || "Profile"}
            className="w-full h-full object-cover"
            onError={() => setBroken(true)}
            referrerPolicy="no-referrer"
          />
        ) : (
          <span aria-hidden>{initials}</span>
        )}
      </div>
      {displayName ? (
        <span className="text-sm font-medium text-foreground dark:text-white truncate max-w-[240px]">
          {displayName}
        </span>
      ) : null}
    </div>
  )
}

function resolveUrl(rawUrl: string | undefined): string {
  if (typeof window === "undefined") return rawUrl ?? ""
  if (!rawUrl) return window.location.href
  if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://")) return rawUrl
  if (rawUrl.startsWith("/")) return `${window.location.origin}${rawUrl}`
  return rawUrl
}

async function copyToClipboard(text: string): Promise<boolean> {
  if (!text) return false
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {}
  }
  try {
    if (typeof document === "undefined") return false
    const el = document.createElement("textarea")
    el.value = text
    el.setAttribute("readonly", "true")
    el.style.position = "fixed"
    el.style.top = "-1000px"
    document.body.appendChild(el)
    el.select()
    const ok = document.execCommand("copy")
    document.body.removeChild(el)
    return ok
  } catch {
    return false
  }
}

async function shareInstagramCardImage(
  dataUrl: string,
  filename: string,
  titleForShare: string | undefined,
  copyFallback: () => Promise<void>,
  toast: ReturnType<typeof useToast>["toast"],
): Promise<void> {
  try {
    const res = await fetch(dataUrl)
    const blob = await res.blob()
    const file = new File([blob], filename, { type: "image/png" })
    const canShareFile =
      typeof navigator !== "undefined" && "share" in navigator && navigator.canShare?.({ files: [file] })
    if (canShareFile) {
      await navigator.share({
        files: [file],
        title: titleForShare,
      })
      toast({
        title: "Ready to share",
        description: "Check your camera roll and share to Instagram.",
      })
    } else {
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.download = filename
      link.href = blobUrl
      link.click()
      URL.revokeObjectURL(blobUrl)
      toast({
        title: "Story card downloaded",
        description: "Open Instagram and share the image to your story.",
      })
    }
  } catch (err) {
    if ((err as Error)?.name !== "AbortError") {
      await copyFallback()
      toast({
        title: "Link copied!",
        description: "Open Instagram and paste in your story or DM.",
      })
    }
  }
}

export interface ShareActionsOptions {
  url?: string
  title?: string
  image?: string
  price?: number
  currency?: string
  variant?: "item" | "profile"
  username?: string
  bio?: string
  storyData?: { description?: string; condition?: string; seller?: string }
  listingMeta?: ListingShareMeta
  onNativeShareSuccess?: () => void
}

export function useShareActions(opts: ShareActionsOptions) {
  const { url, title, image, price, currency, variant = "item", username, bio, storyData, listingMeta, onNativeShareSuccess } =
    opts
  const { toast } = useToast()
  const [currentUrl, setCurrentUrl] = React.useState("")
  const [copied, setCopied] = React.useState(false)
  const [cardPreviewDataUrl, setCardPreviewDataUrl] = React.useState<string | null>(null)
  const [instagramLoading, setInstagramLoading] = React.useState(false)

  React.useEffect(() => {
    setCurrentUrl(resolveUrl(url))
  }, [url])

  const storyContext = React.useMemo<StoryCardContext | null>(() => {
    if (variant !== "item" || !image || !title) return null
    return resolveStoryContext(
      {
        imageUrl: image,
        title,
        seller: storyData?.seller ?? "You",
        listingUrl: resolveUrl(url) || undefined,
        currency,
        price: price ?? 0,
      },
      listingMeta,
    )
  }, [variant, image, title, price, currency, storyData?.seller, url, listingMeta])

  const profileCardInput = React.useMemo((): ProfileStoryCardInput | null => {
    if (variant !== "profile" || !username) return null
    const profileUrl = resolveUrl(url)
    if (!profileUrl) return null
    return {
      profileUrl,
      username,
      avatarUrl: image,
      bio,
    }
  }, [variant, username, url, image, bio])

  React.useEffect(() => {
    let cancelled = false
    if (variant === "item" && storyContext) {
      generateStoryCardDataUrl(storyContext)
        .then((dataUrl) => {
          if (!cancelled) setCardPreviewDataUrl(dataUrl)
        })
        .catch(() => {
          if (!cancelled) setCardPreviewDataUrl(null)
        })
      return () => {
        cancelled = true
      }
    }
    if (variant === "profile" && profileCardInput) {
      generateProfileStoryCardDataUrl(profileCardInput)
        .then((dataUrl) => {
          if (!cancelled) setCardPreviewDataUrl(dataUrl)
        })
        .catch(() => {
          if (!cancelled) setCardPreviewDataUrl(null)
        })
      return () => {
        cancelled = true
      }
    }
    setCardPreviewDataUrl(null)
    return () => {
      cancelled = true
    }
  }, [variant, storyContext, profileCardInput])

  const handleCopy = React.useCallback(async () => {
    const ok = await copyToClipboard(currentUrl)
    if (!ok) {
      toast({ title: "Copy failed", description: "Please copy the link manually.", variant: "destructive" })
      return
    }
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2000)
  }, [currentUrl, toast])

  const handleWhatsApp = React.useCallback(() => {
    window.open(`https://wa.me/?text=${encodeURIComponent(currentUrl)}`, "_blank", "noopener,noreferrer")
  }, [currentUrl])

  const handleMessenger = React.useCallback(() => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
      "_blank",
      "noopener,noreferrer"
    )
  }, [currentUrl])

  const handleTwitter = React.useCallback(() => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(title || "Check this out!")}&url=${encodeURIComponent(currentUrl)}`,
      "_blank",
      "noopener,noreferrer"
    )
  }, [currentUrl, title])

  const handleTelegram = React.useCallback(() => {
    window.open(`https://t.me/share/url?url=${encodeURIComponent(currentUrl)}`, "_blank", "noopener,noreferrer")
  }, [currentUrl])

  const handleInstagram = React.useCallback(async () => {
    if (variant === "item" && image && title) {
      setInstagramLoading(true)
      try {
        const ctx = resolveStoryContext(
          {
            imageUrl: image,
            title,
            seller: storyData?.seller ?? "",
            listingUrl: currentUrl || undefined,
            currency,
            price: price ?? 0,
          },
          listingMeta,
        )
        const dataUrl = await generateStoryCardDataUrl(ctx)
        const slug = title.replace(/\s+/g, "-").toLowerCase().replace(/[^a-z0-9-]/g, "")
        const filename = `kraahl-story-${slug || "listing"}.png`
        await shareInstagramCardImage(dataUrl, filename, title, handleCopy, toast)
      } catch (err) {
        if ((err as Error)?.name !== "AbortError") {
          await handleCopy()
          toast({
            title: "Link copied!",
            description: "Open Instagram and paste in your story or DM.",
          })
        }
      } finally {
        setInstagramLoading(false)
      }
      return
    }
    if (variant === "profile" && username) {
      setInstagramLoading(true)
      try {
        const handle = username.replace(/^@+/, "")
        const dataUrl = await generateProfileStoryCardDataUrl({
          profileUrl: currentUrl || resolveUrl(url),
          username,
          avatarUrl: image,
          bio,
        })
        const filename = `kraahl-profile-${handle.replace(/[^a-z0-9-]/gi, "-") || "user"}.png`
        await shareInstagramCardImage(dataUrl, filename, title, handleCopy, toast)
      } catch (err) {
        if ((err as Error)?.name !== "AbortError") {
          await handleCopy()
          toast({
            title: "Link copied!",
            description: "Open Instagram and paste in your story or DM.",
          })
        }
      } finally {
        setInstagramLoading(false)
      }
      return
    }
    await handleCopy()
    toast({
      title: "Link copied!",
      description: "Open Instagram and paste in your story or DM.",
    })
  }, [variant, image, title, price, currency, storyData?.seller, currentUrl, toast, handleCopy, listingMeta, username, url, bio])

  const handleTikTok = React.useCallback(() => {
    handleCopy()
    toast({ title: "Link copied!", description: "Open TikTok and paste the link." })
  }, [handleCopy, toast])

  const handleEmailDefault = React.useCallback(() => {
    const subject = encodeURIComponent(title || "Check this out!")
    const body = encodeURIComponent(currentUrl)
    window.location.href = `mailto:?subject=${subject}&body=${body}`
  }, [currentUrl, title])

  const handleNativeShare = React.useCallback(async () => {
    if (typeof navigator !== "undefined" && navigator.share && currentUrl) {
      try {
        await navigator.share({ url: currentUrl, title: title })
        onNativeShareSuccess?.()
      } catch (err: unknown) {
        if ((err as Error)?.name !== "AbortError") {
          toast({ title: "Share failed", description: "Please try another option.", variant: "destructive" })
        }
      }
    }
  }, [currentUrl, title, toast, onNativeShareSuccess])

  return {
    currentUrl,
    copied,
    cardPreviewDataUrl,
    instagramLoading,
    handleCopy,
    handleWhatsApp,
    handleMessenger,
    handleTwitter,
    handleTelegram,
    handleInstagram,
    handleTikTok,
    handleEmailDefault,
    handleNativeShare,
  }
}

export interface ShareContentProps {
  currentUrl: string
  copied: boolean
  title?: string
  image?: string
  price?: number
  currency?: string
  variant?: "item" | "profile"
  username?: string
  bio?: string
  cardPreviewDataUrl?: string | null
  instagramLoading?: boolean
  onCopy: () => void
  onWhatsApp: () => void
  onMessenger: () => void
  onTwitter: () => void
  onTelegram: () => void
  onInstagram: () => void
  onTikTok: () => void
  onEmailClick: () => void
  onMore: () => void
}

export function ShareContent({
  currentUrl,
  copied,
  title,
  image,
  price,
  currency,
  variant = "item",
  username,
  bio,
  cardPreviewDataUrl,
  instagramLoading,
  onCopy,
  onWhatsApp,
  onMessenger,
  onTwitter,
  onTelegram,
  onInstagram,
  onTikTok,
  onEmailClick,
  onMore,
}: ShareContentProps) {
  const isProfile = variant === "profile"
  /* These three were behind a `dark` prop that BOTH call sites (share sheet,
     creation-flow success step) passed unconditionally — the page branch was
     dead, so it is gone. The surviving strings are already `light dark:dark`
     pairs, i.e. the target form for the overlay light-theme migration.
     @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md §7 */
  const labelClass = "text-muted-foreground dark:text-white/70 text-xs"
  const inputClass = "flex-1 bg-transparent border-none text-muted-foreground dark:text-white/70 text-sm"
  const copyBtnClass = "text-foreground hover:bg-accent dark:text-white dark:hover:bg-white/10"

  const isItem = variant === "item"
  const showCardPreview = Boolean(cardPreviewDataUrl)
  const showCardSkeleton =
    !cardPreviewDataUrl &&
    ((isItem && image != null && title != null) || (isProfile && username != null))

  return (
    <>
      <div className="flex justify-center mb-4">
        {showCardPreview && cardPreviewDataUrl ? (
          <div className="w-full max-w-[132px] aspect-[9/16] rounded-2xl overflow-hidden border border-white/10 bg-black shrink-0">
            <img
              src={cardPreviewDataUrl}
              alt="Story card preview"
              className="w-full h-full object-cover object-top"
            />
          </div>
        ) : showCardSkeleton ? (
          <div className="w-full max-w-[132px] aspect-[9/16] rounded-2xl overflow-hidden border border-white/10 bg-black shrink-0 flex flex-col">
            <Skeleton className="h-[65%] w-full shrink-0 rounded-none bg-white/10" />
            <div className="flex-1 min-h-0 flex flex-col bg-black">
              <div className="flex-1 min-h-0 flex gap-1 px-1.5 py-2">
                <div className="flex-1 min-w-0 flex flex-col gap-1 justify-start">
                  <Skeleton className="h-3 w-2/5 rounded-sm bg-white/20" />
                  <Skeleton className="h-2.5 w-full rounded-sm bg-white/10" />
                  <Skeleton className="h-2.5 w-4/5 rounded-sm bg-white/10" />
                  <Skeleton className="h-2 w-1/3 rounded-sm bg-white/10 mt-0.5" />
                </div>
                <div className="flex flex-col items-center shrink-0">
                  <Skeleton className="w-9 h-9 rounded-md bg-white/10" />
                  <Skeleton className="h-1.5 w-8 rounded-full bg-white/10 mt-1" />
                </div>
              </div>
              <div className="flex justify-end px-1.5 pb-1.5 pt-0">
                <Skeleton className="h-2.5 w-10 rounded-sm bg-white/10" />
              </div>
            </div>
          </div>
        ) : (
          <div className="w-32 aspect-[3/4] rounded-2xl overflow-hidden relative">
            <img
              // `w-32 aspect-[3/4]` = a 128 CSS px preview tile -> CARD_SM, the
              // aspect-preserving compact-grid rung (the `object-cover` above
              // does the crop). Was the full upload.
              // Ternary, not `|| fallback` on the result: `image` is optional
              // here (this branch renders with only `title` set), and the
              // placeholder is what "no image" means. Previously the nullish
              // fell THROUGH `imagePresetUrl` and was caught by `||` on the way
              // out — same pixels, but it typed the argument as possibly
              // `undefined`.
              src={image ? imagePresetUrl(image, CARD_SM) : "/placeholder.svg?height=256&width=160"}
              alt={title || "Item preview"}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute bottom-3 left-3 right-3">
              <p className="text-white text-xs font-medium line-clamp-2 leading-tight mb-1">{title || "Item"}</p>
              {price !== undefined && (
                <p className="text-white text-sm font-bold">{formatPrice(price, currency ?? "USD")}</p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-4 gap-3 mb-4">
        <button
          type="button"
          onClick={onInstagram}
          disabled={instagramLoading}
          className="flex flex-col items-center gap-2"
        >
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] flex items-center justify-center">
            {instagramLoading ? (
              <Loader2 className="w-7 h-7 text-white animate-spin" />
            ) : (
              <Instagram className="w-7 h-7 text-white" />
            )}
          </div>
          <span className={labelClass}>Instagram</span>
        </button>

        <button type="button" onClick={onWhatsApp} className="flex flex-col items-center gap-2">
          <div className="w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center">
            <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </div>
          <span className={labelClass}>WhatsApp</span>
        </button>

        <button type="button" onClick={onMessenger} className="flex flex-col items-center gap-2">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#00B2FF] to-[#006AFF] flex items-center justify-center p-2">
            <img src={MESSENGER_LOGO_URL} alt="" className="w-full h-full object-contain" />
          </div>
          <span className={labelClass}>Messenger</span>
        </button>

        <button type="button" onClick={onTikTok} className="flex flex-col items-center gap-2">
          <div className="w-14 h-14 rounded-full bg-black flex items-center justify-center border border-white/20">
            <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
            </svg>
          </div>
          <span className={labelClass}>TikTok</span>
        </button>

        <button type="button" onClick={onTwitter} className="flex flex-col items-center gap-2">
          <div className="w-14 h-14 rounded-full bg-black flex items-center justify-center">
            <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </div>
          <span className={labelClass}>X</span>
        </button>

        <button type="button" onClick={onTelegram} className="flex flex-col items-center gap-2">
          <div className="w-14 h-14 rounded-full bg-[#0088cc] flex items-center justify-center">
            <Send className="w-7 h-7 text-white" />
          </div>
          <span className={labelClass}>Telegram</span>
        </button>

        {/* Email / More are the only two tiles whose disc is the PANEL tint
            rather than a brand colour, so they take `*-foreground/*` and follow
            the panel; the branded discs above keep `text-white` because it is
            correct paint over saturated fill in both themes. */}
        <button type="button" onClick={onEmailClick} className="flex flex-col items-center gap-2">
          <div className="w-14 h-14 rounded-full bg-foreground/10 flex items-center justify-center">
            <Mail className="w-7 h-7 text-foreground" />
          </div>
          <span className={labelClass}>Email</span>
        </button>

        <button type="button" onClick={onMore} className="flex flex-col items-center gap-2">
          <div className="w-14 h-14 rounded-full bg-foreground/10 flex items-center justify-center">
            <MoreHorizontal className="w-7 h-7 text-foreground" />
          </div>
          <span className={labelClass}>More</span>
        </button>
      </div>

      <div className="flex items-center gap-2 bg-muted dark:bg-white/5 rounded-xl p-3">
        <Input value={currentUrl} readOnly className={inputClass} />
        <Button onClick={onCopy} variant="ghost" size="sm" className={copyBtnClass}>
          {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
        </Button>
      </div>
    </>
  )
}
